/**
 * Access-control service — the app-level approved-user gate.
 *
 * After Supabase auth establishes a session, the AccessGate consults this
 * service to decide whether the logged-in person is actually allowed into the
 * app. The rule is simple and central:
 *
 *   status = 'approved'   -> admitted
 *   status = 'pending'    -> "awaiting approval" screen
 *   status = 'suspended'  -> "access revoked" screen
 *   no row                -> treated as pending (a request is filed)
 *
 * The backing table is `public.app_access` (migration 00413). A DB trigger
 * files every new signup as 'pending'; superadmins approve / set role / suspend
 * from Config > Access Requests. See lib/productionIdentity.ts for how an
 * approved user's identity + role are then resolved.
 */

import { supabase } from '@/lib/supabase'
import { normalizeAllowedModules, isAdminAccessRole } from '@/config/moduleEntitlements'
import { normalizeAllowedProjects } from '@/config/projectEntitlements'
import { isEntraAuthEnabled } from '@/lib/entraAuth'
import type { ModuleKey } from '@/config/tesseractConfig'

export type AccessStatus = 'pending' | 'approved' | 'suspended'

/** Roles a superadmin can grant on approval. Mirrors the SystemRole values the
 *  identity bind understands (see deriveAppRoleFromSystemRole). */
export const ACCESS_ROLE_OPTIONS = [
  { value: 'GlobalAdmin', label: 'Global Admin (superuser)' },
  { value: 'OrgAdmin', label: 'Organization Admin' },
  { value: 'GlobalViewer', label: 'Global Viewer (read-only)' },
  { value: 'ConfigAdmin', label: 'Config Admin' },
  { value: 'PortfolioManager', label: 'Portfolio Manager' },
  { value: 'Viewer', label: 'Viewer' },
] as const

export type AccessRole = (typeof ACCESS_ROLE_OPTIONS)[number]['value']

export interface AccessRecord {
  email: string
  status: AccessStatus
  accessRole: string
  fullName: string | null
  organizationName: string | null
  organizationId: string | null
  authUserId: string | null
  requestedAt: string | null
  decidedAt: string | null
  decidedBy: string | null
  notes: string | null
  /** Module entitlement (migration 00521). null = no restriction. */
  allowedModules: ModuleKey[] | null
  /** Project entitlement (migration 00541). null = no restriction; [] = none assigned yet. */
  allowedProjects: string[] | null
  /** Projects whose agile pages are read-only for this user (migration 00582). null = none. */
  agileReadOnlyProjects: string[] | null
}

function rowToRecord(r: Record<string, unknown>): AccessRecord {
  return {
    email: String(r.email ?? ''),
    status: (r.status as AccessStatus) ?? 'pending',
    accessRole: (r.access_role as string) ?? 'Viewer',
    fullName: (r.full_name as string) ?? null,
    organizationName: (r.organization_name as string) ?? null,
    organizationId: (r.organization_id as string) ?? null,
    authUserId: (r.auth_user_id as string) ?? null,
    requestedAt: (r.requested_at as string) ?? null,
    decidedAt: (r.decided_at as string) ?? null,
    decidedBy: (r.decided_by as string) ?? null,
    notes: (r.notes as string) ?? null,
    allowedModules: normalizeAllowedModules(r.allowed_modules),
    allowedProjects: normalizeAllowedProjects(r.allowed_projects),
    agileReadOnlyProjects: normalizeAllowedProjects(r.agile_read_only_projects),
  }
}

/** The allowed_projects column is not there yet (00541 not applied). */
function missingProjectsColumn(error: { message?: string } | null): boolean {
  return !!error && /allowed_projects/i.test(error.message ?? '')
}

/**
 * Resolve the CURRENT logged-in user's access record by email. Returns null
 * when no row exists yet (a brand-new account whose signup trigger has not run,
 * or an edge case) — the gate treats null as "pending" and files a request.
 */
export async function fetchMyAccess(email: string): Promise<AccessRecord | null> {
  const { data, error } = await supabase
    .from('app_access')
    .select('*')
    .eq('email', email.toLowerCase())
    .maybeSingle()
  if (error) throw error
  if (!data) return null
  return rowToRecord(data as Record<string, unknown>)
}

/**
 * File a pending access request for the current user (idempotent). The DB
 * trigger normally does this on signup; this is a client-side fallback so the
 * admin queue never misses a knock even if the trigger did not fire.
 */
export async function ensurePendingRequest(
  email: string,
  fullName?: string | null,
  organizationName?: string | null,
): Promise<void> {
  const lower = email.toLowerCase()
  const existing = await fetchMyAccess(lower)
  if (existing) return
  const { error } = await supabase.from('app_access').insert({
    email: lower,
    status: 'pending',
    access_role: 'Viewer',
    full_name: fullName ?? null,
    organization_name: organizationName ?? null,
  })
  // A race with the trigger can produce a duplicate-key error — that is fine,
  // it means the request already exists.
  if (error && !/duplicate key|23505/i.test(error.message)) throw error
}

/**
 * Entra builds: tell the server about this sign-in. The server reads the
 * identity from the verified token, maps the person's tenant to its
 * organization and, when a tenant rule in Setup > Directory says so, approves
 * them automatically (audited). Otherwise it files the pending request.
 * Returns the resulting status, or null when the server does not handle it
 * (non-Entra deployments) so the caller falls back to ensurePendingRequest.
 */
export async function entraFirstSignIn(): Promise<AccessStatus | null> {
  const res = await fetch('/api/directory/sign-in', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
  const body = (await res.json().catch(() => null)) as { status?: AccessStatus; skipped?: boolean; error?: string } | null
  if (!res.ok) throw new Error(body?.error ?? `Sign-in check failed (${res.status})`)
  if (!body || body.skipped || !body.status) return null
  return body.status
}

// ---------------------------------------------------------------------------
// Admin operations (RLS-gated to superadmins by is_app_admin())
// ---------------------------------------------------------------------------

/** List every access record, newest requests first. Admin-only surface. */
export async function listAccess(): Promise<AccessRecord[]> {
  const { data, error } = await supabase
    .from('app_access')
    .select('*')
    .order('requested_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map((r) => rowToRecord(r as Record<string, unknown>))
}

/**
 * Approve an account (optionally setting its role) and stamp the decision.
 * `allowedProjects` is what the admin chose; when nothing was chosen the
 * account is approved with no projects ([]), so it sees nothing until an admin
 * ticks projects. Pass the record's current list to keep it.
 */
export async function approveAccess(
  email: string,
  accessRole: string,
  decidedBy: string,
  allowedProjects: string[] | null = null,
): Promise<void> {
  const decision = {
    status: 'approved',
    access_role: accessRole,
    decided_at: new Date().toISOString(),
    decided_by: decidedBy,
  }
  const lower = email.toLowerCase()
  let { error } = await supabase
    .from('app_access')
    .update({ ...decision, allowed_projects: allowedProjects ?? [] })
    .eq('email', lower)
  // Before 00541 is applied the column does not exist: approve without it.
  if (missingProjectsColumn(error)) ({ error } = await supabase.from('app_access').update(decision).eq('email', lower))
  if (error) throw error
  await closeAccessRequestNotifications(lower, 'Approved')
}

/**
 * Set the projects a user can see. null clears the restriction (every
 * project); [] means none yet; a list limits them to exactly those projects.
 */
export async function setAllowedProjects(email: string, projects: string[] | null): Promise<void> {
  const { error } = await supabase
    .from('app_access')
    .update({ allowed_projects: projects })
    .eq('email', email.toLowerCase())
  if (missingProjectsColumn(error)) {
    throw new Error('Project access needs database migration 00541, which has not been applied yet.')
  }
  if (error) throw error
}

// ---------------------------------------------------------------------------
// Read-only agile access (migration 00582). Admin-only by RLS, like the rest.
// ---------------------------------------------------------------------------

function missingAgileColumn(error: { message?: string } | null): boolean {
  return !!error && /agile_read_only_projects/i.test(error.message ?? '')
}

const AGILE_COLUMN_MISSING = 'Read-only agile access needs database migration 00582, which has not been applied yet.'

/** Approved people holding read-only access to this project's agile pages. */
export async function listAgileReadOnlyGrants(projectId: string): Promise<AccessRecord[]> {
  const { data, error } = await supabase
    .from('app_access')
    .select('*')
    .contains('agile_read_only_projects', [projectId])
    .order('email')
  if (missingAgileColumn(error)) throw new Error(AGILE_COLUMN_MISSING)
  if (error) throw error
  return (data ?? []).map((r) => rowToRecord(r as Record<string, unknown>))
}

/**
 * Give an approved, non-admin person read-only access to a project's agile
 * pages. Refuses anyone who is not approved: access only goes to validated users.
 */
export async function grantAgileReadOnly(email: string, projectId: string): Promise<void> {
  const lower = email.toLowerCase()
  const { data, error } = await supabase.from('app_access').select('*').eq('email', lower).maybeSingle()
  if (error) throw error
  if (!data) throw new Error(`${lower} has no Tesseract access record.`)
  const rec = rowToRecord(data as Record<string, unknown>)
  if (rec.status !== 'approved') throw new Error(`${lower} is not an approved user.`)
  if (isAdminAccessRole(rec.accessRole)) throw new Error(`${lower} is an administrator, so they cannot be limited to read-only access.`)
  const list = rec.agileReadOnlyProjects ?? []
  if (list.includes(projectId)) return
  const { error: upErr } = await supabase
    .from('app_access')
    .update({ agile_read_only_projects: [...list, projectId] })
    .eq('email', lower)
  if (missingAgileColumn(upErr)) throw new Error(AGILE_COLUMN_MISSING)
  if (upErr) throw upErr
}

/** Remove a person's read-only access to a project's agile pages. */
export async function revokeAgileReadOnly(email: string, projectId: string): Promise<void> {
  const lower = email.toLowerCase()
  const { data, error } = await supabase.from('app_access').select('agile_read_only_projects').eq('email', lower).maybeSingle()
  if (missingAgileColumn(error)) throw new Error(AGILE_COLUMN_MISSING)
  if (error) throw error
  const list = normalizeAllowedProjects((data as Record<string, unknown> | null)?.agile_read_only_projects) ?? []
  const next = list.filter((id) => id !== projectId)
  const { error: upErr } = await supabase
    .from('app_access')
    .update({ agile_read_only_projects: next.length ? next : null })
    .eq('email', lower)
  if (upErr) throw upErr
}

/**
 * Ask the server to tell the admins that the signed-in user is waiting for
 * access (in-app notification and email). The server reads the request
 * itself and sends once per request, so calling it on every pending screen is
 * safe. Never throws: the gate must not fail on a notification.
 */
export async function notifyAccessRequested(): Promise<void> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    // Entra builds: the bootstrap fetch interceptor attaches the bearer.
    if (!isEntraAuthEnabled()) {
      const { data } = await supabase.auth.getSession()
      const token = data.session?.access_token
      if (!token) return
      headers.Authorization = `Bearer ${token}`
    }
    await fetch('/api/access/request-notify', { method: 'POST', headers, body: '{}' })
  } catch { /* best effort */ }
}

/** Mark the admins' "access requested" notifications for this person as handled. */
async function closeAccessRequestNotifications(email: string, outcome: 'Approved' | 'Suspended'): Promise<void> {
  await supabase
    .from('notifications')
    .update({ read: true, action_taken: outcome })
    .eq('entity_type', 'access_request')
    .eq('entity_id', email)
    .is('action_taken', null)
    .then(() => undefined, () => undefined)
}

/** Suspend (revoke) an account. The user is bounced to the "revoked" screen
 *  on their next request. */
export async function suspendAccess(email: string, decidedBy: string): Promise<void> {
  const { error } = await supabase
    .from('app_access')
    .update({
      status: 'suspended',
      decided_at: new Date().toISOString(),
      decided_by: decidedBy,
    })
    .eq('email', email.toLowerCase())
  if (error) throw error
  await closeAccessRequestNotifications(email.toLowerCase(), 'Suspended')
}

/** Change an approved user's role without touching their status. */
export async function setAccessRole(email: string, accessRole: string): Promise<void> {
  const { error } = await supabase
    .from('app_access')
    .update({ access_role: accessRole })
    .eq('email', email.toLowerCase())
  if (error) throw error
}

/**
 * Set a user's module entitlement. null clears the restriction (every module
 * the deployment enables); a list limits them to exactly those modules. An
 * empty list is refused here and by the table's check constraint, since it
 * would lock the user out of everything while still reading as restricted.
 */
export async function setAllowedModules(email: string, modules: ModuleKey[] | null): Promise<void> {
  if (modules !== null && modules.length === 0) {
    throw new Error('Choose at least one module, or All modules (no restriction).')
  }
  const { error } = await supabase
    .from('app_access')
    .update({ allowed_modules: modules })
    .eq('email', email.toLowerCase())
  if (error) throw error
}

/** Remove an access record entirely (e.g. a bad-actor request). */
export async function deleteAccess(email: string): Promise<void> {
  const { error } = await supabase
    .from('app_access')
    .delete()
    .eq('email', email.toLowerCase())
  if (error) throw error
}

/**
 * Add / invite an account to the approved list directly (admin-driven). Creates
 * an approved row keyed by email; when that person later signs up, the signup
 * trigger binds their auth_user_id to this existing row (on conflict), so they
 * land approved rather than pending.
 */
export async function inviteApprovedUser(
  email: string,
  accessRole: string,
  fullName: string | null,
  decidedBy: string,
): Promise<void> {
  const row = {
    email: email.toLowerCase(),
    status: 'approved',
    access_role: accessRole,
    full_name: fullName,
    decided_at: new Date().toISOString(),
    decided_by: decidedBy,
  }
  // Like an approval, an invited person sees no projects until an admin ticks
  // them (admins are never restricted, so they get no list).
  const isAdminRole = accessRole === 'GlobalAdmin' || accessRole === 'OrgAdmin'
  let { error } = await supabase.from('app_access').upsert(isAdminRole ? row : { ...row, allowed_projects: [] }, { onConflict: 'email' })
  if (missingProjectsColumn(error)) ({ error } = await supabase.from('app_access').upsert(row, { onConflict: 'email' }))
  if (error) throw error
}
